import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { User } from '../user.model';
import { UserService } from '../user.service';
import { environment } from '../../environments/environment';
import { LogService } from '../log.service';
import { File360Error } from '../error.model';
import { HttpStatus } from '../error-handler.service';
import { Config } from '../config.model';
import { ConfigService } from '../config.service';

@Component({
  selector: 'file360-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LoginFormComponent implements OnInit {

  //
  // ViewChild elements needed to programmatically set focus on these fields.
  //
  @ViewChild('inputUser', { static: true })
  inputUser: any;
  @ViewChild('inputPassword', { static: true })
  inputPassword: any;

  public password = "";

  constructor(private router: Router, private route: ActivatedRoute, private userService: UserService, private log: LogService, private configService: ConfigService) { }

  model = new User();
  year = environment.year;
  showForgotPassword: boolean = false;
  get isAuthenticationError(): boolean {
    return this.authenticationErrorMessage.length > 0;
  }
  authenticationErrorMessage: string = "";
  isLoggingOn: boolean = false;
  config: Config | undefined;

  ngOnInit() {
    //
    // Check for old-style search URL search request
    //
    if (this.route.snapshot.queryParamMap.has("retrievalinput")) {
      //
      // Old style URL used query parameters, not route parameters.
      // The existance of "retrievalinput" query parameter will trigger the handling
      // of converting the old URL to the new URL format.
      // The "template" parameter was also a query parameter, now it's used
      // as a routing segment.
      // Add search=1 query parameter so template knows to immediately perform the search.
      //
      this.router.navigate(['/home/retrieve',
        {
          outlets:
          {
            template: [this.route.snapshot.queryParamMap.get("template"), this.route.snapshot.queryParams],
            retrieve: ["results"]
          }
        }]);
    }
    else {

      // save the last name for use in error conditions 
      const userName = this.userService.currentUser.Name;
      this.userService.logOff();
      this.userService.currentUser.Name = userName;

      //
      // Load app config and set local object.
      //
      this.configService.loadConfig().subscribe(
        config => {
          this.config = config as Config;
          this.configService.configuration = this.config;

          //
          // Default the default system text from config file.
          // Any actual error text will replace this text.
          //
          if (this.config.welcomeMessage != null && this.config.welcomeMessage.length > 0) {
            this.log.logMessage(`welcomeMessage is '${this.config.welcomeMessage}'`);
          }

          if (this.config.forgotPasswordMessage != null && this.config.forgotPasswordMessage.length > 0) {
            this.log.logMessage(`forgotPasswordMessage is '${this.config.forgotPasswordMessage}'`);
          }

          this.setLastError();
        },
        error => {
          this.log.logMessage(`Error loading config : '${error.Message}'`);
          console.error(error);
        });

      this.setLastError();
    }

  }

  private setLastError() {
    if (this.userService.lastErrorMessage.length > 0) {
      this.authenticationErrorMessage = this.userService.lastErrorMessage;
      this.clearCredentialsAndSetFocus();
    }
  }

  onSubmit() {

    //
    // Reset flags to defaults
    //
    this.showForgotPassword = false;
    this.authenticationErrorMessage = "";
    this.isLoggingOn = true;

    this.log.logMessage("onSubmit() fired");

    //
    // Invoke logOn, handle response
    //
    this.userService.logOn(this.model.Name, this.password, 'en-US').subscribe(() => {
      //
      // User successfully logged on.
      // Determine which component to load first.

      //
      // If returnUrl is specified, use it.
      // Otherwise, default to dashboard (home).
      //
      let redirect = this.userService.redirectUrl ? this.userService.redirectUrl : '/home';

      this.log.logMessage(`Redirecting to ${redirect}`);

      //
      // Using navigateByUrl, not navigate, so all route and query parameters are maintained
      //
      this.router.navigateByUrl(redirect);

    },
      (f360Error: File360Error) => { // this is the projects Error not the typescript Error
        //
        // Error occurred. Clear isLoggingOn flag to allow for re-attempt.
        //
        this.isLoggingOn = false;

        //
        // Display error message
        //
        this.authenticationErrorMessage = f360Error.Message.replace("\r\n", "<br>");

        if (f360Error.Id == HttpStatus.Unauthorized) {
          //
          // Show instructions on how to reset password.
          //
          this.showForgotPassword = true;
        }

        //
        // Clear user name, password and set focus into field.
        //
        this.clearCredentialsAndSetFocus();

        throw new File360Error(f360Error.Id, f360Error.Message, new Error(f360Error.Err.message));
      });
  }

  forgotPassword() {
    //
    // Toggle the showForgotPassword boolean which shows or hides the 'forgot password text' on the dialog.
    //
    this.showForgotPassword = !this.showForgotPassword;
  }

  clearCredentialsAndSetFocus() {
    // Interaction guidelines request to keep last attempted user name
    // in the form and clear only the password. Set focus in the password field.
    this.model.Name = this.userService.currentUser.Name;
    this.password = "";
    setTimeout(() => {
      this.inputPassword.nativeElement.focus();
    });
  }
}
